import {
  ErrorMessage,
  Field,
  FormApi,
  Label,
} from '@maternity/mun-forms/horizontal';
import { addClass } from '@maternity/mun-forms/horizontal/utils';
import { Field as VerticalField } from '@maternity/mun-forms/vertical';
import * as React from 'react';

import { FieldName, getModel } from './BraintreeClient';

type LabelProps = React.ComponentProps<typeof Label>;
interface HostedInputProps {
  // The braintree fields are "virtual", so this component doesn't need to
  // constrain the FieldValues
  form: FormApi<any>;
  name: FieldName;
  children?: React.ReactNode;
  className?: string;
}
type HostedFieldProps = Omit<React.ComponentProps<'span'>, 'id'> &
  HostedInputProps & {
    label: NonNullable<LabelProps['children']>;
    labelProps?: Omit<LabelProps, 'children'>;
  };

const invalidErrorMessages = {
  cvv: 'Invalid CVV',
  expirationDate: 'Invalid expiration date (Must be of form MM/YYYY)',
  number: 'Invalid card number',
};

const getBrainTreeFieldId = (name: FieldName) => `braintree-field-${name}`;

const HostedInput = ({ form, name, children, ...props }: HostedInputProps) => {
  const errorMessages = {
    invalid_braintree: invalidErrorMessages[name],
    value_error: invalidErrorMessages[name],
  };
  return (
    <>
      <span
        id={getBrainTreeFieldId(name)}
        braintree-field={name}
        {...addClass(props, 'form-control')}
      >
        {children}
      </span>
      <ErrorMessage
        form={form}
        model={getModel(name)}
        errorMessages={errorMessages}
      />
    </>
  );
};

/**
 * Renders a horizontal form group with an empty container for braintree to
 * create a hosted field inside.
 *
 * Needs to be passed as `children` to `Braintree`.
 */
export const HostedField = ({
  form,
  label,
  labelProps = {},
  name,
  children,
  ...props
}: HostedFieldProps) => (
  <Field
    required={true}
    htmlFor={getBrainTreeFieldId(name)}
    label={label}
    labelProps={labelProps}
  >
    <HostedInput form={form} name={name} {...props}>
      {children}
    </HostedInput>
  </Field>
);

/**
 * Renders a vertical form group with an empty container for braintree to
 * create a hosted field inside.
 *
 * Needs to be passed as `children` to `Braintree`.
 */
export const HostedVerticalField = ({
  form,
  label,
  labelProps = {},
  name,
  children,
  ...props
}: HostedFieldProps) => (
  <VerticalField
    required={true}
    htmlFor={getBrainTreeFieldId(name)}
    label={label}
    labelProps={labelProps}
  >
    <HostedInput form={form} name={name} {...props}>
      {children}
    </HostedInput>
  </VerticalField>
);
